<template>
	<div>
		<v-card v-show="visible">
			<v-card-title align="left" :id="id">
				<v-row dense class="text-subtitle-1">
					<v-col cols="3">
						<v-img :src="item.icon" max-height="32" max-width="32" contain position="left center"></v-img>
					</v-col>
					<v-col cols="9" class="integration-card-expanded">
						<div>{{item.name}}</div>
					</v-col>
				</v-row>
			</v-card-title>
			<v-card-text>
				<v-row dense justify="center" align="center">
					<v-col cols="8">
						<template v-if="displayState === 'deploying'">
							<v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />Deploying…
						</template>
						<template v-else-if="displayState === 'deploy-errored'">
							<span class="error--text">Failed</span>
							<v-tooltip v-if="deployInfo" bottom>
								<template v-slot:activator="{ on, attrs }"><v-icon small color="error" class="ml-1" v-bind="attrs" v-on="on">mdi-alert-circle-outline</v-icon></template>
								<span>{{ deployInfo }}</span>
							</v-tooltip>
						</template>
						<span v-else-if="displayState === 'running'" class="success--text">Enabled</span>
						<span v-else>Installed</span>
					</v-col>
					<v-col cols="4">
						<v-dialog v-model="disable" :max-width="600" persistent>
							<template v-slot:activator="{ on, attrs }">
								<v-btn v-show="item.enabled" x-small text color="primary" v-bind="attrs" v-on="on" >
									Disable
								</v-btn>
							</template>
							<DisableIntegration :item="item" @close="disable=false"/>
						</v-dialog>
						<v-dialog v-model="enable" :max-width="1150" persistent>
							<template v-slot:activator="{ on, attrs }">
								<v-btn v-show="!item.enabled && !item.isVirtual" x-small text color="primary" v-bind="attrs" v-on="on" >
									Enable
								</v-btn>
							</template>
							<EnableIntegration v-if="enable" :item="item" @close="enable=false"/>
						</v-dialog>
						<v-btn v-show="displayState === 'deploy-errored'" x-small text color="error" :loading="retrying" @click="retry">
							Retry
						</v-btn>
					</v-col>
				</v-row>
				<v-row v-if="item.enabled && schemas.length > 0 || item.isVirtual" dense justify="center" align="center" class="mt-3">
					<v-col :cols="(type === 'input') ? 8 : 12" class="text-overline">
						Connections 
					</v-col>
					<v-col v-if="(type === 'input')" cols="4">
						<v-dialog v-model="dialog" :max-width="1150" persistent>
							<template v-slot:activator="{ on, attrs }">
								<v-btn x-small text color="primary" v-bind="attrs" v-on="on" >
									Add
								</v-btn>
							</template>
							<ConnectionDialog :item="item" @close="dialog=false"/>
						</v-dialog>
						
					</v-col>
				</v-row>
				<template v-if="item.enabled || item.isVirtual">
					<v-row v-for="(schema, i) in schemas" :key="schema.cls" dense :class="{ 'flex-row-reverse': (type === 'output') }">
						<v-col cols="9">
							{{schema.name}}
						</v-col>
						<v-col cols="3">
							<v-menu v-if="type === 'input' && schema.connections" v-model="menus[schema.cls]" bottom offset-y :close-on-content-click="false">
								<template v-slot:activator="{ on, attrs }">
									<v-btn x-small text plain v-bind="attrs" v-on="on">
										<ConnectionChip :item="item" :type="type" :schema="schema" />
									</v-btn>
								</template>
								<ConnectionInfo :item="item" :schema="schema" @close="menus[schema.cls]=false" />
							</v-menu>
							<ConnectionChip v-if="type === 'output' && schema.connections" :item="item" :type="type" :schema="schema" />
						</v-col>
					</v-row>
				</template>
				<v-row v-if="item.enabled && item.implementation.supportsVirtual" dense justify="center" align="center" class="mt-3">
					<v-col cols="8"></v-col>
					<v-col cols="4">
						<v-dialog v-model="virtualConnections" :max-width="1150" persistent>
							<template v-slot:activator="{ on, attrs }">
								<v-btn x-small text color="primary" v-bind="attrs" v-on="on">
									Manage Virtual
								</v-btn>
							</template>
							<VirtualConnectionsDialog :item="item" @close="virtualConnections=false"/>
						</v-dialog>
					</v-col>
				</v-row>
				<v-row v-if="!item.enabled" dense justify="center" align="center">
					<v-col cols="8"></v-col> 
					<v-col cols="4">
						<v-dialog v-model="remove" :max-width="600" persistent>
							<template v-slot:activator="{ on, attrs }">
								<v-btn v-show="!item.enabled" x-small text color="primary" v-bind="attrs" v-on="on" >
									Remove
								</v-btn>
							</template>
							<RemoveIntegration :item="item" @close="remove=false"/>
						</v-dialog>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
		<v-card v-show="!visible" height="50px" style="opacity: .40">
			<v-card-title align="left" class="py-0">
				<v-row>
					<v-col cols="3">
						<v-img :src="item.icon" max-height="25" max-width="100" contain position="left center"></v-img>
					</v-col>
					<v-col cols="9" class="text-subtitle-1 integration-card-collapsed">
						<div>{{item.name}}</div>
					</v-col>
				</v-row>
			</v-card-title>
		</v-card>
	</div>
</template>

<script>

import RemoveIntegration from '@/components/configure/RemoveIntegration';
import DisableIntegration from '@/components/configure/DisableIntegration';
import EnableIntegration from '@/components/configure/EnableIntegration';
import ConnectionDialog from '@/components/connections/ConnectionDialog';
import ConnectionInfo from '@/components/connections/ConnectionInfo';
import ConnectionChip from '@/components/connections/ConnectionChip';
import VirtualConnectionsDialog from '@/components/configure/VirtualConnectionsDialog';
import { mapState } from 'vuex';
import _ from 'lodash';
import config from '@/api/config';

export default {
	name: "IntegrationCard",

	components: {
		RemoveIntegration,
		DisableIntegration,
		EnableIntegration,
		ConnectionDialog,
		ConnectionInfo,
		ConnectionChip,
		VirtualConnectionsDialog
	},
	props: {
		type: String,
		item: Object
	},

	computed: {
		...mapState('configuration', ['inputsVisible', 'outputsVisible']),
		visible()
		{
			//show or hide depending on if connection lines are being drawn
			if(this.type === 'input')
			{
				return (!this.inputsVisible || this.inputsVisible.indexOf(this.item.integrationId) > -1)
			} else {
				return (!this.outputsVisible || this.outputsVisible.indexOf(this.item.integrationId) > -1)
			}
			
		},
		schemas()
		{
			return this.item.implementation[`${this.type}s`];
		},
		id()
		{
			return `${this.type}-${this.item.integrationId}`;
		},
		// Live deploy state for display. Falls back to the static enabled flag until
		// the first status poll resolves.
		displayState()
		{
			if (this.deployState) return this.deployState;
			return this.item.enabled ? 'running' : 'disabled';
		}
	},
	

	data: () => ({
		dialog: false,
		enable: false,
		disable: false,
		remove: false,
		virtualConnections: false,
		menus:{},
		deployState: null,
		deployInfo: null,
		statusTimer: null,
		retrying: false,
		// While true we've just triggered a deploy and ignore an early stale "running"
		// (ArgoCD hasn't observed the new sync yet) until the real deploy state appears.
		awaitingDeploy: false,
		deployDeadline: 0
	}),

	watch: {
		// Enabling/disabling flips item.enabled (the enable action reloads the list).
		// Start tracking deploy status when it turns on, reset when it turns off.
		'item.enabled'(enabled)
		{
			if (enabled) {
				this.beginDeployTracking();
			} else {
				this.stopPolling();
				this.awaitingDeploy = false;
				this.deployState = null;
				this.deployInfo = null;
			}
		}
	},

	mounted()
	{
		if (this.item.enabled) this.fetchStatus();
	},

	beforeDestroy()
	{
		this.stopPolling();
	},

	methods: {
		// Called when a deploy was just triggered (enable / retry). Show "deploying"
		// optimistically and poll, riding out a stale "running" until the deploy appears.
		beginDeployTracking()
		{
			this.deployState = 'deploying';
			this.deployInfo = null;
			this.awaitingDeploy = true;
			this.deployDeadline = Date.now() + 30000;
			this.fetchStatus();
		},

		async fetchStatus()
		{
			let state, info;
			try {
				({ state, info } = await config.getStatus(this.item.integrationId));
			} catch (e) {
				// The status endpoint can hiccup right after enable (the ArgoCD app may not
				// exist yet). Keep showing "deploying" and let the next poll resolve it.
				if (!this.deployState) this.deployState = 'deploying';
				this.managePolling();
				return;
			}

			const infoLower = (info || '').toLowerCase();
			if (infoLower.includes('building container') || infoLower.includes('waiting for deployment')) {
				// The container is still building / waiting on the deployment but the
				// deploy has effectively gone through. Treat it as deployed so the card
				// never gets stuck on "Deploying…" -- this lets us recover from stuck states.
				this.awaitingDeploy = false;
				this.deployState = 'running';
				this.deployInfo = null;
			} else if (this.awaitingDeploy && state === 'running' && Date.now() < this.deployDeadline) {
				// Stale "running" from before the just-triggered sync was observed by ArgoCD.
				// Hold on "deploying" and keep polling until the real deploy state appears.
				this.deployState = 'deploying';
			} else {
				// Real deploy state observed (or grace elapsed) -- follow the backend.
				this.awaitingDeploy = false;
				this.deployState = state;
				this.deployInfo = info;
			}

			this.managePolling();
		},

		managePolling()
		{
			// Poll while deploying, or while still waiting for a just-triggered deploy to show.
			if (this.deployState === 'deploying' || this.awaitingDeploy) {
				if (!this.statusTimer) {
					this.statusTimer = setInterval(() => this.fetchStatus(), 5000);
				}
			} else {
				this.stopPolling();
			}
		},

		stopPolling()
		{
			if (this.statusTimer) {
				clearInterval(this.statusTimer);
				this.statusTimer = null;
			}
		},

		async retry()
		{
			this.retrying = true;
			try {
				await this.$store.dispatch('configuration/enableIntegration', {
					integrationId: this.item.integrationId,
					rebuild: true
				});
				this.beginDeployTracking();
			} catch (e) {
				// Leave it in the errored state so the user can retry again.
			} finally {
				this.retrying = false;
			}
		}
	}
};
</script>

<style>
.integration-card-collapsed div {
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
}

.integration-card-expanded {
	word-break: normal;
}
</style>